<template>
  <v-dialog persistent width="1200" v-model="stagingDialog">
    <v-card class="custom-card" rounded="0">
      <!-- Toolbar -->
      <v-toolbar color="secondary" class="white--text">
        <v-btn icon class="white--text mr-2" @click="$emit('back')">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title class="font-weight-bold">
          {{ $t('farm.importReview') || 'Import Review — Staging' }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-chip small class="mr-3" color="white" text-color="secondary">
          {{ validRows }} / {{ totalRows }} rows valid
        </v-chip>
        <v-btn icon class="white--text" @click="$emit('cancel')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- Summary Banner -->
      <div class="staging-summary px-6 py-4">
        <v-row dense>
          <v-col cols="12" sm="3">
            <div class="summary-card summary-total">
              <div class="summary-number">{{ totalRows }}</div>
              <div class="summary-label">Total Records</div>
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="summary-card summary-valid">
              <div class="summary-number">{{ validRows }}</div>
              <div class="summary-label">Valid</div>
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="summary-card summary-warn">
              <div class="summary-number">{{ warnRows }}</div>
              <div class="summary-label">Warnings</div>
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="summary-card summary-error">
              <div class="summary-number">{{ errorRows }}</div>
              <div class="summary-label">Errors</div>
            </div>
          </v-col>
        </v-row>
      </div>

      <!-- Filter Tabs -->
      <div class="px-6 pb-2">
        <v-chip-group v-model="activeFilter" mandatory active-class="secondary" class="filter-chips">
          <v-chip filter value="all" small>All ({{ totalRows }})</v-chip>
          <v-chip filter value="valid" small color="success">
            <v-icon left x-small>mdi-check-circle</v-icon>Valid ({{ validRows }})
          </v-chip>
          <v-chip filter value="warnings" small color="warning">
            <v-icon left x-small>mdi-alert</v-icon>Warnings ({{ warnRows }})
          </v-chip>
          <v-chip filter value="errors" small color="error">
            <v-icon left x-small>mdi-close-circle</v-icon>Errors ({{ errorRows }})
          </v-chip>
        </v-chip-group>
      </div>

      <!-- Issue Legend -->
      <div class="px-6 pb-3">
        <v-row dense>
          <v-col>
            <div class="legend-row">
              <span class="legend-item">
                <v-icon x-small color="error">mdi-map-marker-off</v-icon>
                Format Invalid
              </span>
              <span class="legend-item">
                <v-icon x-small color="warning">mdi-earth-off</v-icon>
                Outside Country
              </span>
              <span class="legend-item">
                <v-icon x-small color="info">mdi-crosshairs-gps</v-icon>
                Geofence Fallback
              </span>
              <span class="legend-item">
                <v-icon x-small color="orange">mdi-content-copy</v-icon>
                Duplicate
              </span>
              <span class="legend-item">
                <v-icon x-small color="error">mdi-vector-polygon</v-icon>
                Self-Intersection
              </span>
              <span class="legend-item">
                <v-icon x-small color="warning">mdi-swap-horizontal</v-icon>
                Reversed lat/lng
              </span>
              <span class="legend-item ml-4" style="border-left:1px solid #e0e0e0;padding-left:12px">
                <v-icon x-small color="secondary">mdi-pencil</v-icon>
                Fixable — click to edit
              </span>
            </div>
          </v-col>
        </v-row>
      </div>

      <!-- Data Table -->
      <div class="staging-table-container px-4">
        <v-data-table
          :headers="tableHeaders"
          :items="filteredRows"
          :items-per-page="10"
          :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
          dense
          class="staging-table elevation-0"
        >
          <!-- Row # -->
          <template v-slot:item.rowIndex="{ item }">
            <span class="text--secondary caption">{{ item.rowIndex }}</span>
          </template>

          <!-- Farm Name -->
          <template v-slot:item.farmName="{ item }">
            <span class="font-weight-medium">{{ item.farmName || '—' }}</span>
          </template>

          <!-- Coordinates -->
          <template v-slot:item.coordinates="{ item }">
            <span v-if="item.lat && item.log" class="caption mono">
              {{ item.lat }}, {{ item.log }}
            </span>
            <span v-else class="caption text--disabled">—</span>
          </template>

          <!-- Validation Issues -->
          <template v-slot:item.issues="{ item }">
            <div class="issue-chips">
              <!-- Format Invalid -->
              <v-tooltip v-if="item.issues.formatInvalid" bottom>
                <template v-slot:activator="{ on }">
                  <v-chip x-small color="error" class="mr-1 mb-1" v-on="on">
                    <v-icon left x-small>mdi-map-marker-off</v-icon>
                    Format
                  </v-chip>
                </template>
                <span>{{ item.issues.formatInvalid }}</span>
              </v-tooltip>

              <!-- Outside Country Boundary -->
              <v-tooltip v-if="item.issues.outsideBoundary" bottom>
                <template v-slot:activator="{ on }">
                  <v-chip x-small color="warning" class="mr-1 mb-1" v-on="on">
                    <v-icon left x-small>mdi-earth-off</v-icon>
                    Boundary
                  </v-chip>
                </template>
                <span>{{ item.issues.outsideBoundary }}</span>
              </v-tooltip>

              <!-- Geofence Fallback -->
              <v-tooltip v-if="item.issues.geofenceFallback" bottom>
                <template v-slot:activator="{ on }">
                  <v-chip x-small color="info" dark class="mr-1 mb-1" v-on="on">
                    <v-icon left x-small>mdi-crosshairs-gps</v-icon>
                    Geofence
                  </v-chip>
                </template>
                <span>{{ item.issues.geofenceFallback }}</span>
              </v-tooltip>

              <!-- Duplicate in file -->
              <v-tooltip v-if="item.issues.duplicateInFile" bottom>
                <template v-slot:activator="{ on }">
                  <v-chip x-small color="orange" dark class="mr-1 mb-1" v-on="on">
                    <v-icon left x-small>mdi-content-copy</v-icon>
                    Dup (File)
                  </v-chip>
                </template>
                <span>{{ item.issues.duplicateInFile }}</span>
              </v-tooltip>

              <!-- Duplicate in DB -->
              <v-tooltip v-if="item.issues.duplicateInDb" bottom>
                <template v-slot:activator="{ on }">
                  <v-chip x-small color="deep-orange" dark class="mr-1 mb-1" v-on="on">
                    <v-icon left x-small>mdi-database-alert</v-icon>
                    Dup (DB)
                  </v-chip>
                </template>
                <span>{{ item.issues.duplicateInDb }}</span>
              </v-tooltip>

              <!-- Self-Intersection -->
              <v-tooltip v-if="item.issues.selfIntersect" bottom>
                <template v-slot:activator="{ on }">
                  <v-chip x-small color="error" class="mr-1 mb-1" v-on="on">
                    <v-icon left x-small>mdi-vector-polygon</v-icon>
                    Self-Intersect
                  </v-chip>
                </template>
                <span>{{ item.issues.selfIntersect }}</span>
              </v-tooltip>

              <!-- Geofence reversed lat/lng order (swap fixes it) -->
              <v-tooltip v-if="item.issues.geofenceOutsideBoundary && isGeofenceReversible(item)" bottom>
                <template v-slot:activator="{ on }">
                  <v-chip x-small color="warning" class="mr-1 mb-1" v-on="on">
                    <v-icon left x-small>mdi-swap-horizontal</v-icon>
                    Geofence Order
                  </v-chip>
                </template>
                <span>{{ item.issues.geofenceOutsideBoundary }}</span>
              </v-tooltip>

              <!-- Geofence genuinely outside country boundary -->
              <v-tooltip v-else-if="item.issues.geofenceOutsideBoundary" bottom>
                <template v-slot:activator="{ on }">
                  <v-chip x-small color="warning" class="mr-1 mb-1" v-on="on">
                    <v-icon left x-small>mdi-earth-off</v-icon>
                    Outside Country
                  </v-chip>
                </template>
                <span>{{ item.issues.geofenceOutsideBoundary }}</span>
              </v-tooltip>

              <span v-if="!hasAnyIssue(item)" class="caption success--text">
                <v-icon x-small color="success">mdi-check-circle</v-icon> OK
              </span>
            </div>
          </template>

          <!-- Status badge -->
          <template v-slot:item.status="{ item }">
            <v-icon v-if="rowSeverity(item) === 'error'" color="error" small>mdi-close-circle</v-icon>
            <v-icon v-else-if="rowSeverity(item) === 'warning'" color="warning" small>mdi-alert-circle</v-icon>
            <v-icon v-else color="success" small>mdi-check-circle</v-icon>
          </template>

          <!-- Fix Actions -->
          <template v-slot:item.actions="{ item }">
            <div class="d-flex align-center">
              <!-- Reverse lat/lng order (geofence in [lat, lng] instead of [lng, lat]) -->
              <v-tooltip
                v-if="isGeofenceReversible(item)"
                bottom
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    x-small
                    color="secondary"
                    class="mr-1"
                    v-on="on"
                    @click="reverseGeofence(item)"
                  >
                    <v-icon x-small>mdi-swap-horizontal</v-icon>
                  </v-btn>
                </template>
                <span>Reverse geofence lat/lng order</span>
              </v-tooltip>

              <!-- Fix coordinates (boundary or DB duplicate) -->
              <v-tooltip
                v-if="isCoordFixable(item)"
                bottom
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    x-small
                    color="secondary"
                    class="mr-1"
                    v-on="on"
                    @click="openCoordEdit(item)"
                  >
                    <v-icon x-small>mdi-map-marker-radius</v-icon>
                  </v-btn>
                </template>
                <span>Fix coordinates</span>
              </v-tooltip>

              <!-- Fix polygon (self-intersection) -->
              <v-tooltip
                v-if="isPolygonFixable(item)"
                bottom
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    x-small
                    color="secondary"
                    v-on="on"
                    @click="openPolygonEdit(item)"
                  >
                    <v-icon x-small>mdi-vector-polygon</v-icon>
                  </v-btn>
                </template>
                <span>Fix polygon</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </div>

      <!-- Action Footer -->
      <v-card-actions class="px-6 py-4 staging-footer">
        <div class="footer-info">
          <template v-if="errorRows > 0">
            <v-icon color="error" small class="mr-1">mdi-alert-circle</v-icon>
            <span class="caption error--text">
              {{ errorRows }} row(s) have critical errors and will be skipped during import.
            </span>
          </template>
          <template v-if="warnRows > 0">
            <v-icon color="warning" small class="mr-1 ml-3">mdi-alert</v-icon>
            <span class="caption warning--text">
              {{ warnRows }} row(s) have warnings but will still be imported.
            </span>
          </template>
          <template v-if="editedCount > 0">
            <v-icon color="secondary" small class="mr-1 ml-3">mdi-pencil-check</v-icon>
            <span class="caption secondary--text">
              {{ editedCount }} row(s) manually edited.
            </span>
          </template>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          v-if="errorRows + warnRows > 0"
          text
          color="error"
          :disabled="loading"
          class="mr-2"
          @click="downloadErrorRows"
        >
          <v-icon left small>mdi-download</v-icon>
          Download Errors ({{ errorRows + warnRows }})
        </v-btn>
        <v-btn text @click="$emit('back')" :disabled="loading" class="mr-2">
          <v-icon left small>mdi-arrow-left</v-icon>
          Back
        </v-btn>
        <v-btn
          color="secondary"
          :disabled="validRows === 0 || loading"
          :loading="loading"
          @click="confirmImport"
          elevation="0"
        >
          <v-icon left small>mdi-upload</v-icon>
          Import {{ validRows + warnRows }} Records
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Coordinate Edit Dialog -->
    <FarmCoordinateEditDialog
      v-model="coordEditOpen"
      :row="coordEditRow"
      @saved="onCoordSaved"
    />

    <!-- Polygon Edit Dialog -->
    <FarmPolygonEditDialog
      v-model="polygonEditOpen"
      :row="polygonEditRow"
      :geofence="activePolygonGeofence"
      @saved="onPolygonSaved"
    />
  </v-dialog>
</template>

<script>
import Papa from 'papaparse';
import FarmCoordinateEditDialog from './FarmCoordinateEditDialog';
import FarmPolygonEditDialog from './FarmPolygonEditDialog';

// Key used in _raw for geofence coordinates (matches backend attributeMapping.geofence)
const RAW_GEOFENCE_KEY = 'Geofence Coordinates';

export default {
  name: 'FarmBulkImportStagingDialog',

  components: { FarmCoordinateEditDialog, FarmPolygonEditDialog },

  props: {
    value: {
      type: Boolean,
      required: true,
    },
    stagingData: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      activeFilter: 'all',

      // rowEdits: { [rowIndex]: { lat?, lng?, geofence? } }
      // Accumulated edits sent to backend on confirm
      rowEdits: {},

      // Coordinate edit dialog
      coordEditOpen: false,
      coordEditRow: {},

      // Polygon edit dialog
      polygonEditOpen: false,
      polygonEditRow: {},

      tableHeaders: [
        { text: '#', value: 'rowIndex', width: '55px', sortable: false },
        { text: 'Status', value: 'status', width: '70px', sortable: false },
        { text: 'Farm Name', value: 'farmName', sortable: true },
        { text: 'Farmer', value: 'farmerName', sortable: true },
        { text: 'Country', value: 'country', sortable: true },
        { text: 'Coordinates (lat, lng)', value: 'coordinates', sortable: false },
        { text: 'Validation Issues', value: 'issues', sortable: false, width: '320px' },
        { text: 'Fix', value: 'actions', sortable: false, width: '70px' },
      ],
    };
  },

  watch: {
    stagingData(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.rowEdits = {};
        this.coordEditOpen = false;
        this.coordEditRow = {};
        this.polygonEditOpen = false;
        this.polygonEditRow = {};
      }
    },
  },

  computed: {
    stagingDialog: {
      get() { return this.value; },
      set(val) { this.$emit('input', val); },
    },

    totalRows() { return this.stagingData.length; },
    validRows() { return this.stagingData.filter(r => this.rowSeverity(r) === 'valid').length; },
    warnRows() { return this.stagingData.filter(r => this.rowSeverity(r) === 'warning').length; },
    errorRows() { return this.stagingData.filter(r => this.rowSeverity(r) === 'error').length; },
    editedCount() { return Object.keys(this.rowEdits).length; },

    filteredRows() {
      if (this.activeFilter === 'valid') return this.stagingData.filter(r => this.rowSeverity(r) === 'valid');
      if (this.activeFilter === 'warnings') return this.stagingData.filter(r => this.rowSeverity(r) === 'warning');
      if (this.activeFilter === 'errors') return this.stagingData.filter(r => this.rowSeverity(r) === 'error');
      return this.stagingData;
    },

    // Current geofence for the active polygon edit row:
    // prefer user-edited version, fall back to _raw from backend
    activePolygonGeofence() {
      const ri = this.polygonEditRow && this.polygonEditRow.rowIndex;
      if (!ri) return [];
      const edit = this.rowEdits[ri];
      if (edit && edit.geofence) return edit.geofence;
      return (this.polygonEditRow._raw && this.polygonEditRow._raw[RAW_GEOFENCE_KEY]) || [];
    },
  },

  methods: {
    hasAnyIssue(row) {
      const i = row.issues || {};
      return !!(i.formatInvalid || i.outsideBoundary || i.geofenceFallback || i.duplicateInFile || i.duplicateInDb || i.selfIntersect || i.geofenceOutsideBoundary);
    },

    rowSeverity(row) {
      const i = row.issues || {};
      if (i.formatInvalid || i.duplicateInDb || i.selfIntersect) return 'error';
      if (i.outsideBoundary || i.duplicateInFile || i.geofenceFallback || i.geofenceOutsideBoundary) return 'warning';
      return 'valid';
    },

    // A row is coord-fixable if it has boundary or DB duplicate issues (not format invalid)
    isCoordFixable(row) {
      const i = row.issues || {};
      return !!(i.outsideBoundary || i.duplicateInDb || i.duplicateInFile) && !i.formatInvalid;
    },

    // A row is polygon-fixable if it has a self-intersection issue
    isPolygonFixable(row) {
      return !!(row.issues && row.issues.selfIntersect);
    },

    // Resolve the current geofence for a row: user-edited version first, else
    // the raw backend value (parsed array, or a JSON string we parse here).
    currentGeofence(row) {
      const edit = this.rowEdits[row.rowIndex];
      if (edit && edit.geofence) return edit.geofence;
      let g = row._raw && row._raw[RAW_GEOFENCE_KEY];
      if (typeof g === 'string') {
        try { g = JSON.parse(g); } catch { g = null; }
      }
      return Array.isArray(g) ? g : [];
    },

    // Reversible only when the backend detected the [lat, lng] swap case (message
    // says so) and there's an actual polygon to flip.
    isGeofenceReversible(row) {
      const msg = row.issues && row.issues.geofenceOutsideBoundary;
      return !!msg && /\[lat, lng\] order/.test(msg) && this.currentGeofence(row).length >= 3;
    },

    // ── Reverse geofence lat/lng order ──────────────────────────────────────
    reverseGeofence(row) {
      const geofence = this.currentGeofence(row);
      if (!geofence.length) return;

      // Swap the first two elements of each pair (preserve any extras), turning
      // [lat, lng] into the expected [lng, lat].
      const swapped = geofence.map((c) =>
        Array.isArray(c) && c.length >= 2 ? [c[1], c[0], ...c.slice(2)] : c
      );

      // Clear the issue; the swapped geofence rides along in rowEdits and
      // replaces the parsed value on the import request (same path as polygon edit).
      this.$set(row.issues, 'geofenceOutsideBoundary', null);

      this.$set(this.rowEdits, row.rowIndex, {
        ...(this.rowEdits[row.rowIndex] || {}),
        geofence: swapped,
      });
    },

    // ── Coordinate edit ────────────────────────────────────────────────────
    openCoordEdit(row) {
      this.coordEditRow = row;
      this.coordEditOpen = true;
    },

    onCoordSaved({ rowIndex, lat, lng }) {
      const row = this.stagingData.find(r => r.rowIndex === rowIndex);
      if (!row) return;

      // Update display values reactively
      this.$set(row, 'lat', lat);
      this.$set(row, 'log', lng);

      // Clear the issues that the coordinate fix addresses
      this.$set(row.issues, 'outsideBoundary', null);
      this.$set(row.issues, 'duplicateInDb', null);
      this.$set(row.issues, 'duplicateInFile', null);

      // Track edit for backend
      this.$set(this.rowEdits, rowIndex, {
        ...(this.rowEdits[rowIndex] || {}),
        lat,
        lng,
      });
    },

    // ── Polygon edit ───────────────────────────────────────────────────────
    openPolygonEdit(row) {
      this.polygonEditRow = row;
      this.polygonEditOpen = true;
    },

    onPolygonSaved({ rowIndex, geofence }) {
      const row = this.stagingData.find(r => r.rowIndex === rowIndex);
      if (!row) return;

      // Clear self-intersection issue
      this.$set(row.issues, 'selfIntersect', null);

      // Track edit for backend
      this.$set(this.rowEdits, rowIndex, {
        ...(this.rowEdits[rowIndex] || {}),
        geofence,
      });
    },

    // ── Confirm ───────────────────────────────────────────────────────────
    confirmImport() {
      const rowsToImport = this.stagingData.filter(r => this.rowSeverity(r) !== 'error');
      this.$emit('confirm', rowsToImport, this.rowEdits);
    },

    // ── Download error rows ───────────────────────────────────────────────
    downloadErrorRows() {
      const CSV_HEADERS = [
        'I am not a farmer',
        'User ID of Farmer',
        'Farmer Registration ID',
        'Farmer ID',
        'Farmer First Name',
        'Farmer Middle Name',
        'Farmer Last Name',
        'Farm ID',
        'Farm Name',
        'Farm Registration ID',
        'Nationall Farm ID',
        'Ownership Type',
        'Production Type',
        'Country',
        'Province/State',
        'Village/Town/City',
        'Street Number',
        'Latitude',
        'Longitude',
        'Geofence Coordinates',
        'Geofence Name',
        'Geofence Category',
        'Geofence Type',
        'Geofence Center Latitude',
        'Geofence Center Longitude',
        'Farm Area in Hectares',
        'National Farm ID',
        'Validation Issues',
      ];

      const problemRows = this.stagingData.filter(r => this.rowSeverity(r) !== 'valid');

      const rows = problemRows.map(row => {
        const raw = row._raw || {};
        const obj = {};

        CSV_HEADERS.slice(0, -1).forEach(h => {
          const val = raw[h];
          // Geofence may have been parsed into an array by the backend
          obj[h] = Array.isArray(val) ? JSON.stringify(val) : (val != null ? val : '');
        });

        const i = row.issues || {};
        const issueMessages = [
          i.formatInvalid,
          i.outsideBoundary,
          i.geofenceFallback,
          i.duplicateInFile,
          i.duplicateInDb,
          i.selfIntersect,
          i.geofenceOutsideBoundary,
        ].filter(Boolean);
        obj['Validation Issues'] = issueMessages.join('; ');

        return obj;
      });

      const csv = Papa.unparse({ fields: CSV_HEADERS, data: rows });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `import_errors_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.staging-summary {
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}

.summary-card {
  background: #fff;
  border-radius: 8px;
  padding: 12px 16px;
  text-align: center;
  border-left: 4px solid #ccc;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}

.summary-total  { border-color: #607d8b; }
.summary-valid  { border-color: #4caf50; }
.summary-warn   { border-color: #ff9800; }
.summary-error  { border-color: #f44336; }

.summary-number {
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
}
.summary-label {
  font-size: 11px;
  color: #757575;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-top: 4px;
}
.summary-total  .summary-number { color: #607d8b; }
.summary-valid  .summary-number { color: #4caf50; }
.summary-warn   .summary-number { color: #ff9800; }
.summary-error  .summary-number { color: #f44336; }

.filter-chips { flex-wrap: wrap; }

.legend-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  padding: 6px 0;
  border-top: 1px solid #f0f0f0;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  color: #757575;
}

.staging-table-container {
  max-height: 380px;
  overflow-y: auto;
}

.staging-table ::v-deep th {
  background: #fafafa !important;
  font-size: 11px !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #616161 !important;
}

.issue-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 4px 0;
}

.mono {
  font-family: 'Courier New', monospace;
  font-size: 11px;
}

.staging-footer {
  border-top: 1px solid #e0e0e0;
  background: #fafafa;
  flex-wrap: wrap;
  gap: 8px;
}

.footer-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
</style>
